import {InputProps} from "antd/lib/input";
import React, {useEffect} from "react";
import {Button, Input, Tooltip} from "antd";
import './veriflyt-id-input.style.less';
import {createVeriflytCheckResultValidator, validateVeriflytID} from "./validation";
import {ValueType, AccountLookupResult} from "./types";
import {FormItemProps} from "antd/lib/form";
import {ButtonProps} from "antd/lib/button";
import {WrappedFormUtils} from "antd/lib/form/Form";
import {useAccountLookup} from "./graphql";

type ValidPrefix = 'P' | 'A' | 'B' | 'C' | 'D' | 'E';

export interface VeriflytIdInputProps {
    veriflytPrefix: ValidPrefix
    initialVeriflytId: string,
    value?: ValueType,
    onChange?: (newValue: ValueType) => void,
    disabled?: boolean,
    hideGenBtn?: boolean,
    placeholder?: InputProps['placeholder']
}

const BASE_CLASS = 'veriflyt-id-input';

export default function VeriflytIdInput(props: VeriflytIdInputProps){

    const buttonProps: ButtonProps = {
        disabled: !!props.initialVeriflytId,
        title: props.initialVeriflytId ?
            'Veriflyt ID is already set. You cannot generate a new one.' :
            'Generate Veriflyt ID and register to VeriFlyt',
        onClick: (e) => {
            if ( typeof props.onChange === 'function' ){
                props.onChange({ ...props.value, willGenerate: !props.value?.willGenerate });
            }
        },
        className: !props.value?.willGenerate ? `${BASE_CLASS}-generate-button` : null
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>){
        if (typeof props.onChange === 'function') {
            const newValue = { ...props.value, inputValue: String(e.target.value).toUpperCase() };
            props.onChange(newValue);
        }
    }

    function renderInput(){
        if (!props.value?.willGenerate || !props.onChange){
            if (props.disabled){
                return (
                    <Tooltip title="You do not have permission to edit Veriflyt ID">
                        <Input
                            value={props.value?.inputValue}
                            disabled
                            placeholder={props.placeholder}
                        />
                    </Tooltip>
                )
            }
            return (
                <Input
                    value={props.value?.inputValue}
                    onChange={handleInputChange}
                    allowClear
                    placeholder={props.placeholder}
                />
            )
        }
    }

    function renderGenButton(){
        if (props.disabled || typeof props.onChange !== 'function' || props.hideGenBtn) {
            return null;
        }
        if (!props.value?.willGenerate) {
            return (
                <Button {...buttonProps}>Generate</Button>
            )
        } else {
            return (
                <Button {...buttonProps}>Input Manually</Button>
            )
        }
    }

    const input = renderInput();
    const genButton = renderGenButton();

    if (!input && !genButton){
        return null;
    }

    if (!genButton){
        return input;
    }

    return (
        <Input.Group className={BASE_CLASS} compact>
            {input}
            {genButton}
        </Input.Group>
    )
}

export interface VeriflyIdStateOptions {
    initialVeriflytID: string,
    veriflytPrefix: ValidPrefix,
    form: WrappedFormUtils,
    fieldName: string,
    hasPermission?: boolean
}

export function useVeriflytIdState(options: VeriflyIdStateOptions) {

    const { initialVeriflytID, veriflytPrefix, form, fieldName, hasPermission } = options;

    const currResult = React.useRef<AccountLookupResult>({
        account: null,
        loading: false,
        error: null
    });

    const getCurrResult = React.useCallback(() => {
        return currResult.current;
    }, []);

    const formValue: ValueType = form.getFieldValue(fieldName);

    function onCompleted(){
        form.validateFields([fieldName], { force: true });
    }

    const result = useAccountLookup(formValue?.inputValue, onCompleted);

    useEffect(() => {
        currResult.current = result;
    }, [result]);

    return {
        fieldName,
        veriflytPrefix,
        hasPermission,
        account: result.account,
        get formFieldDecoratorRules() {
            return [
                {
                    validator: validateVeriflytID(veriflytPrefix)
                },
                {
                    required: !!initialVeriflytID,
                    validator: createVeriflytCheckResultValidator(getCurrResult)
                }
            ]
        },
        get formItemProps(): Pick<FormItemProps, 'validateStatus' | 'hasFeedback' | 'help'> {
            const formValue: ValueType = form.getFieldValue(fieldName);
            const fieldErrors = form.getFieldError(fieldName);

            if (result.loading) {
                return {
                    validateStatus: 'validating',
                    hasFeedback: true,
                    help: 'Validating Veriflyt ID...'
                };
            }

            if (hasPermission && !formValue?.inputValue && !formValue?.willGenerate && !fieldErrors) {
                return {
                    validateStatus: '',
                    hasFeedback: false,
                    help: ''
                };
            }
            else if (!hasPermission){
                return {
                    validateStatus: '',
                    hasFeedback: false,
                    help: 'You do not have permission to change Veriflyt ID'
                };
            }

            if (formValue?.willGenerate){
                return {
                    validateStatus: 'success',
                    hasFeedback: true,
                    help: 'Veriflyt ID will be generated upon save'
                }
            }

            if (result.error){
                return {
                    validateStatus: 'error',
                    hasFeedback: true,
                    help: 'Failed to validate Veriflyt ID'
                };
            }

            if (result.account && (result.account.primaryName || result.account.secondaryName) && !fieldErrors) {

                let name = '';

                if (result.account.primaryName) {
                    name += result.account.primaryName;
                }

                if (result.account.secondaryName) {
                    name += ` ${result.account.secondaryName}`;
                }

                if (!hasPermission){
                    name += ' (You do not have permission to change Veriflyt ID)';
                }

                return {
                    validateStatus: 'success',
                    hasFeedback: true,
                    help: name
                };
            }

            if (!result.account && !fieldErrors) {
                return {
                    validateStatus: 'error',
                    hasFeedback: true,
                    help: 'Unknown Veriflyt ID'
                };
            }

            if (fieldErrors) {
                return {
                    validateStatus: 'error',
                    hasFeedback: true,
                    help: fieldErrors.join(', ')
                };
            }
        },
    }
}